import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Helmet from 'react-helmet';
import { parse } from 'query-string';
import LinearProgress from '@material-ui/core/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import Box from 'retired/Box';
import SelectableChip from 'retired/SelectableChip';
import { selectPillars, selectERGs, selectCategories, selectSDGs } from 'entities/cause/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { selectForms } from 'entities/form/selectors';
import { selectPictures } from 'entities/picture/selectors';
import { selectPartners } from 'entities/partners/selectors';
import {
  selectOrganization,
  selectUser,
  selectAccessibleUserLocations,
} from 'entities/user/selectors';
import { showErrorAction } from 'containers/Notification/actions';
import getDeedFeatureForDeedType from 'utils/getDeedFeatureForDeedType';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import PageHeader from 'retired/PageHeader';
import getDeedTypeForDisplay from 'utils/getDeedTypeForDisplay';
import { DeedManagerPrivileges, Privileges } from 'utils/privileges';
import { State } from 'reducers';
import { useDeedFormQueryParams } from 'containers/DeedFormPage/queryParams';

import { selectLoading, selectActiveStep, selectUploadedFile } from './selectors';
import DeedProjectForm from './DeedProjectForm';
import DeedCampaignForm from './DeedCampaignForm';
import DeedVolunteerForm from './DeedVolunteerForm';
import DeedEventForm from './DeedEventForm';
import SharedDeedForm from './SharedDeedForm';
import * as Actions from './actions';
import epics from './epics';
import reducer from './reducer';
import { formSyncErrors, pickFormFields, type DeedTypes } from './formUtils';

const key = 'deedFormPage';

const mapDeedForms = {
  Event: DeedVolunteerForm,
  Campaign: DeedCampaignForm,
  Project: DeedProjectForm,
  BaseEvent: DeedEventForm,
};

const formValuesSelector = pickFormFields([
  'name',
  'categories',
  'partner',
  'status',
  'public',
  'featured',
]);

const DeedFormPage = ({ actions }: Props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { deedId, deedType, paramType, deed, past } = useDeedFormQueryParams();

  const activeStep = useSelector(selectActiveStep);
  const campaigns = useSelector(selectCampaigns);
  const categories = useSelector((state: State) => selectCategories(state, true));
  const ERGs = useSelector(selectERGs);
  const forms = useSelector(selectForms);
  const loading = useSelector(selectLoading);
  const locations = useSelector(selectAccessibleUserLocations);
  const organization = useSelector(selectOrganization);
  const partners = useSelector(selectPartners);
  const pictures = useSelector(selectPictures);
  const pillars = useSelector(selectPillars);
  const SDGs = useSelector(selectSDGs);
  const skills = useSelector(selectSkills);
  const uploadedFile = useSelector(selectUploadedFile);
  const user = useSelector(selectUser);

  const formData = useSelector(formValuesSelector);
  const errors = useSelector(formSyncErrors);
  const { name } = formData;

  const DeedForm = mapDeedForms[deedType] || DeedVolunteerForm;

  const isExternal = deedId.startsWith('ext-');

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    dispatch(Actions.initAction(deedId, !!parse(location.search).duplicate));
  }, []);

  if (loading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }

  const cannotAccessDeed =
    !isExternal &&
    deed &&
    user &&
    (!deed.organization || deed.organization.id !== user.organization.id) &&
    (!deed.partner || (deed.partner.id !== user.organization.id && !deed.isSharedWithSubsidiary));
  const cannotManageDeedType =
    user &&
    (!user.hasFeature(getDeedFeatureForDeedType(deedType)) ||
      !user.hasPrivilege(DeedManagerPrivileges[deedType]));
  const campaignsEnabled = organization?.isCompany() || user?.canDonate();

  if (cannotAccessDeed || cannotManageDeedType || (deedType === 'Campaign' && !campaignsEnabled)) {
    return (
      <Box>
        <p>
          Uh oh!{' '}
          {cannotAccessDeed ? (
            <>Looks like you don&apos;t have access to the requested deed.</>
          ) : (
            <>Looks like you don&apos;t have permission to manage {deedType.toLowerCase()}s.</>
          )}
        </p>
      </Box>
    );
  }

  const deedTypeSelect = (type: string) => history.replace(`/deeds/${type}/edit/new`);

  const infoTooltip: Record<DeedTypes, string | undefined> = {
    Event:
      'Volunteer Events are opportunities for your employees to contribute to greater social good in their community. These events can be associated with non-profit organizations, and hours towards these activities will help you make progress in your cause areas, and Social Development Goals (SDGs).',
    BaseEvent: `Company Events are social, professional, or interest-based activities for your company.
    These events are not associated with non-profit organizations, cause areas, or Social
    Development Goals (SDGs).`,
    Campaign: undefined,
    Project: undefined,
  };

  const title = `${deed ? name : `Create ${past ? 'Past' : 'New'}`} ${getDeedTypeForDisplay(
    deedType
  )}`;

  return (
    <>
      <Helmet title={deed ? name : `Create ${past ? 'Past' : 'New'}`} />
      <PageHeader
        title={
          !infoTooltip[deedType] ? (
            title
          ) : (
            <>
              {title}
              <Tooltip title={infoTooltip[deedType]!} style={{ marginLeft: '12px' }}>
                <InfoOutlined />
              </Tooltip>
            </>
          )
        }
        onArrowClick={() => history.push(`/deeds/${paramType}`)}
      />
      {!deed && !past && !isExternal && (
        <>
          {user?.hasFeature(getDeedFeatureForDeedType('Event')) &&
          user.hasPrivilege(Privileges.EVENT_MANAGE) ? (
            <SelectableChip
              onClick={() => deedTypeSelect('volunteer')}
              label="Volunteer Event"
              selected={deedType === 'Event'}
            />
          ) : null}
          {user?.hasFeature(getDeedFeatureForDeedType('BaseEvent')) &&
          user.hasPrivilege(Privileges.BASE_EVENT_MANAGE) ? (
            <SelectableChip
              onClick={() => deedTypeSelect('events')}
              label="Event"
              selected={deedType === 'BaseEvent'}
            />
          ) : null}
          {campaignsEnabled &&
          user?.hasFeature(getDeedFeatureForDeedType('Campaign')) &&
          user.hasPrivilege(Privileges.FUNDRAISER_MANAGE) ? (
            <SelectableChip
              onClick={() => deedTypeSelect('campaigns')}
              label="Fundraiser"
              selected={deedType === 'Campaign'}
            />
          ) : null}
          {user?.hasFeature(getDeedFeatureForDeedType('Project')) &&
          user.hasPrivilege(Privileges.PROJECT_MANAGE) ? (
            <SelectableChip
              onClick={() => deedTypeSelect('projects')}
              label="Project"
              selected={deedType === 'Project'}
            />
          ) : null}
        </>
      )}

      {deed?.isSharedWithSubsidiary ? (
        <SharedDeedForm locations={locations} isExternal={isExternal} />
      ) : (
        activeStep !== null && (
          <DeedForm
            isPublic={formData.public}
            isFeatured={formData.featured}
            ERGs={ERGs}
            SDGs={SDGs}
            actions={actions}
            activeStep={activeStep}
            campaigns={campaigns}
            categories={categories}
            deed={deed}
            deedType={deedType}
            errors={errors}
            forms={forms}
            locations={locations}
            organization={organization}
            partners={partners}
            past={past}
            pictures={pictures}
            pillars={pillars}
            selectedCategories={formData.categories}
            selectedPartner={formData.partner}
            skills={skills}
            status={formData.status}
            uploadedFile={uploadedFile}
            user={user}
          />
        )
      )}
    </>
  );
};
type Props = {
  actions: unknown;
};

const withConnect = connect(
  () => ({}),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, showErrorAction }, dispatch),
  })
);

export default withConnect(DeedFormPage);
